import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Gauge, Shield, Trophy, Users } from "lucide-react";
import { api, useFetch } from "../lib/api";
import { EmptyState } from "../components/atoms";
import { getTeamWallVisual } from "./teamsWallConfig";
import "./teams.css";

function buildPerformanceRows(team, drivers) {
    const driverPoints = drivers.reduce((sum, driver) => sum + Number(driver.points || 0), 0);
    const driverWins = drivers.reduce((sum, driver) => sum + Number(driver.wins || 0), 0);
    const driverPodiums = drivers.reduce((sum, driver) => sum + Number(driver.podiums || 0), 0);
    const position = team.position || team.standing || "—";

    return [
        { label: "Season Points", value: team.points ?? "—", accent: true },
        { label: "Grid Position", value: `P${position}`, accent: true },
        { label: "Constructor Titles", value: team.championships ?? "—" },
        { label: "Race Wins", value: team.constructor_wins ?? team.wins ?? "—" },
        { label: "Podiums", value: team.podiums ?? "—" },
        { label: "Driver Points", value: driverPoints || "—" },
        { label: "Driver Wins", value: driverWins || "—" },
        { label: "Driver Podiums", value: driverPodiums || "—" },
        { label: "Base", value: team.base || "Not published" },
        { label: "Chassis", value: team.chassis || "Not published" },
        { label: "Engine", value: team.engine || "Not published" },
        { label: "Reliability", value: team.reliability_pct != null ? `${team.reliability_pct}%` : "Not published" },
    ];
}

export default function TeamProfile() {
    const { id } = useParams();
    const { data, loading } = useFetch(() => api.teamById(id), [id]);
    const { data: driversResp } = useFetch(() => api.driversActive(), []);
    const team = data?.team;

    const teamDrivers = useMemo(
        () => (driversResp?.drivers || []).filter((driver) => team?.driver_ids?.includes(driver.id)),
        [driversResp?.drivers, team]
    );

    if (loading) {
        return (
            <div className="w-full">
                <div className="shimmer h-[74vh] w-full rounded-none md:rounded-[34px]" />
            </div>
        );
    }

    if (!team) {
        return (
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <EmptyState title="Team not found" icon={Trophy} />
            </div>
        );
    }

    const visual = getTeamWallVisual(team);
    const primaryColor = team.primary_color || "#E10600";
    const secondaryColor = team.secondary_color || primaryColor;
    const performanceRows = buildPerformanceRows(team, teamDrivers);

    return (
        <div className="w-full" data-testid="team-profile-page">
            <div className="teams-wall-shell min-h-screen w-full">
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background: `
                            radial-gradient(circle at 14% 18%, ${primaryColor}28, transparent 24%),
                            radial-gradient(circle at 86% 24%, ${secondaryColor}22, transparent 28%),
                            radial-gradient(circle at 48% 100%, ${primaryColor}18, transparent 34%)
                        `,
                    }}
                />
                <div className="teams-wall-noise" />

                <div className="relative z-10 px-4 py-6 sm:px-6 lg:px-10">
                    <Link to="/teams" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
                        <ArrowLeft className="h-4 w-4" />
                        Back to teams
                    </Link>

                    <section className="mt-5 overflow-hidden rounded-[34px] border border-white/10 bg-black/20">
                        <div className="relative min-h-[70vh] overflow-hidden">
                            {visual.hero ? (
                                <img
                                    src={visual.hero}
                                    alt={`${team.name} garage image`}
                                    className="absolute inset-0 h-full w-full object-cover object-center"
                                />
                            ) : null}
                            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(4,6,8,0.96),rgba(4,6,8,0.78)_36%,rgba(4,6,8,0.36)_70%,rgba(4,6,8,0.92))]" />
                            <div className="absolute inset-0 grid-bg opacity-20" />

                            <div className="relative z-10 flex min-h-[70vh] flex-col justify-end px-6 py-8 sm:px-8 lg:px-12">
                                <div className="max-w-3xl">
                                    <p className="label-eyebrow text-zinc-300">{visual.badge}</p>
                                    <h1 className="mt-3 font-display text-6xl leading-[0.88] tracking-tight text-white sm:text-7xl lg:text-8xl">
                                        {team.short_name || team.name}
                                    </h1>
                                    <p className="mt-5 max-w-2xl text-base leading-7 text-zinc-300">
                                        {team.description}
                                    </p>
                                    <div className="mt-6 flex flex-wrap items-center gap-3">
                                        <Badge label={`${team.championships || 0}x constructor titles`} />
                                        <Badge label={`${team.constructor_wins || team.wins || 0} race wins`} />
                                        <Badge label={team.base || "garage location pending"} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_420px]">
                        <div className="space-y-6">
                            <div className="rounded-[28px] border border-white/10 bg-black/20 p-5 sm:p-6">
                                <div className="flex items-center gap-2">
                                    <Gauge className="h-4 w-4 text-zinc-400" />
                                    <p className="label-eyebrow">Performance Matrix</p>
                                </div>
                                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                    {performanceRows.map((row) => (
                                        <StatTile key={row.label} label={row.label} value={row.value} accent={row.accent} />
                                    ))}
                                </div>
                            </div>

                            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                                <div className="rounded-[28px] border border-white/10 bg-black/20 p-5 sm:p-6">
                                    <div className="flex items-center gap-2">
                                        <Shield className="h-4 w-4 text-zinc-400" />
                                        <p className="label-eyebrow">Garage Brief</p>
                                    </div>
                                    <div className="mt-5 space-y-4 text-sm leading-7 text-zinc-300">
                                        <p>{team.description}</p>
                                        <p>
                                            Current grid position sits at <span className="text-white">P{team.position || team.standing || "—"}</span> with{" "}
                                            <span className="text-white">{team.points ?? "—"} points</span> delivered by the present lineup.
                                        </p>
                                        <p>
                                            Headquarters: <span className="text-white">{team.base || "Not published"}</span>.
                                        </p>
                                    </div>
                                </div>

                                <div className="rounded-[28px] border border-white/10 bg-black/20 p-5 sm:p-6">
                                    <div className="flex items-center gap-2">
                                        <Users className="h-4 w-4 text-zinc-400" />
                                        <p className="label-eyebrow">Driver Lineup</p>
                                    </div>
                                    <div className="mt-5 space-y-3">
                                        {teamDrivers.map((driver) => (
                                            <Link
                                                key={driver.id}
                                                to={`/drivers/${driver.id}`}
                                                className="block rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-4 transition hover:border-white/20"
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <div>
                                                        <p className="text-lg font-semibold text-white">
                                                            {driver.first_name} {driver.last_name}
                                                        </p>
                                                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                                                            #{driver.number || "—"} • P{driver.position || driver.standing || driver.current_season_position || "—"}
                                                        </p>
                                                    </div>
                                                    <div
                                                        className="h-12 w-1.5 rounded-full"
                                                        style={{ background: `linear-gradient(180deg, ${primaryColor}, ${secondaryColor})` }}
                                                    />
                                                </div>
                                                <div className="mt-4 grid grid-cols-3 gap-2">
                                                    <InlineMetric label="Pts" value={driver.points ?? "—"} />
                                                    <InlineMetric label="Wins" value={driver.wins ?? "—"} />
                                                    <InlineMetric label="Pod" value={driver.podiums ?? "—"} />
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <aside className="space-y-6">
                            <div className="rounded-[28px] border border-white/10 bg-black/20 p-5 sm:p-6">
                                <p className="label-eyebrow">Constructor Identity</p>
                                <div className="mt-5 space-y-4">
                                    <IdentityRow label="Signal" value={visual.signal} />
                                    <IdentityRow label="Status" value={team.status || "active"} />
                                    <IdentityRow label="Wordmark" value={visual.logo} />
                                    <IdentityRow label="Monogram" value={visual.monogram} />
                                    <IdentityRow label="Engine" value={team.engine || "Not published"} />
                                    <IdentityRow label="Chassis" value={team.chassis || "Not published"} />
                                </div>
                            </div>

                            <div className="rounded-[28px] border border-white/10 bg-black/20 p-5 sm:p-6">
                                <p className="label-eyebrow">Wall Image</p>
                                <div className="mt-5 overflow-hidden rounded-[24px] border border-white/10">
                                    {visual.hero ? (
                                        <img
                                            src={visual.hero}
                                            alt={`${team.name} wall art`}
                                            className="h-[360px] w-full object-cover object-center"
                                        />
                                    ) : (
                                        <div className="grid h-[360px] place-items-center bg-white/[0.03]">
                                            <div className="teams-wordmark rounded-[30px] px-6 py-8">
                                                <div className="font-display text-5xl tracking-tight text-white">{visual.logo}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </aside>
                    </section>
                </div>
            </div>
        </div>
    );
}

function Badge({ label }) {
    return (
        <span className="rounded-full border border-white/10 bg-white/[0.08] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-200">
            {label}
        </span>
    );
}

function StatTile({ label, value, accent = false }) {
    return (
        <div className={`rounded-2xl border bg-white/[0.04] px-4 py-4 ${accent ? "border-white/20" : "border-white/10"}`}>
            <p className="label-eyebrow">{label}</p>
            <p className={`mt-2 font-display text-3xl leading-none tracking-tight ${accent ? "text-white" : "text-zinc-100"}`}>
                {value}
            </p>
        </div>
    );
}

function InlineMetric({ label, value }) {
    return (
        <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-2">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
            <p className="mt-1 text-sm font-semibold text-white">{value}</p>
        </div>
    );
}

function IdentityRow({ label, value }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">{label}</p>
            <p className="mt-2 text-sm font-semibold text-white">{value}</p>
        </div>
    );
}
